import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './Dragdrop.css';

/* ===================================================
   ★ CONFIGURATION
   =================================================== */
const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://demo.myssa.net';

// ★ Part de la barre de progression réservée aux uploads (le reste = scripts)
const PART_UPLOAD = 30;

// ★ Zones de dépôt (boxes « Données paiements clients / fournisseurs » supprimées)
//   FILE_NAMES[i] ↔ SAVE_NAMES[i] : garder le même ordre dans les deux listes
const FILE_NAMES = [
  'Base article', 'Commandes clients', 'Commandes fournisseurs', 'Base stock',
  'Offre client', 'Liste emplacements',
];

const SAVE_NAMES = [
  'I donnée client base article.xlsx', 'I donnée client cmde client.xlsx',
  'I donnée client cmde frn.xlsx', 'I donnée client stock.xlsx',
  'I donnée client offre.xlsx', 'I liste emplacement.xlsx',
];

const ALL_SCRIPTS = [
  'analyse_delai_approvisionnement.py', 'animation.py', 'base_de_donne_stock.py',
  'base_de_donnee_cmde_frn.py', 'base_de_donnee_cmde_client.py', 'camembert_1.py',
  'camembert_3.py', 'commande_a_10_jours_frn.py', 'commande_en_retard.py',
  'commandes_frn_sans_ar.py', 'emplacement_multi_ref.py', 'emplacement_vide.py',
  'plan_reception_fournisseur.py', 'commande_a_10_jours.py', 'commande_en_rupture.py',
  'donnee_histogramme.py', 'recalage_cmde_frn.py', 'commande_frn_recalage_stock_fort.py',
  'picking_log.py', 'base_de_donne_item.py', 'plan_expedition_cmde_client.py',
  'graph1.py', 'graph2.py', 'analyse_faisabilite.py', 'offre.py',
  'item_fin_de_mois.py', 'stock_projete.py', 'montant_vente.py',
  'montant_achat.py', 'evolution_stock.py',
];

const TOTAL_SCRIPTS = ALL_SCRIPTS.length;

/* ===================================================
   ICÔNES (SVG au trait, couleur = currentColor)
   =================================================== */
const ICON_PATHS = {
  'Base article': 'M21 8l-9-5-9 5 9 5 9-5zM3 8v8l9 5 9-5V8M12 13v8',
  'Commandes clients': 'M3 4h2l2.4 11.2a2 2 0 002 1.6h7.7a2 2 0 002-1.5L21 8H6M10 21a1 1 0 100-2 1 1 0 000 2zM18 21a1 1 0 100-2 1 1 0 000 2z',
  'Commandes fournisseurs': 'M1 6h13v10H1zM14 10h4l3 3v3h-7zM5.5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM17.5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
  'Base stock': 'M3 21V9l9-6 9 6v12M7 21v-8h10v8M7 17h10',
  'Offre client': 'M9 4h6v3H9zM7 5H5v16h14V5h-2M9 12h6M9 16h4',
  'Liste emplacements': 'M12 21s-7-6.2-7-11a7 7 0 1114 0c0 4.8-7 11-7 11zM12 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
};

const Icon = ({ path, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={path} />
  </svg>
);

const IconCheck = ({ size = 12, width = 3 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12l5 5L20 7" />
  </svg>
);

const IconCross = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/* ===================================================
   COMPOSANT
   =================================================== */
const Dragdrop = () => {
  const [files, setFiles] = useState(Array(FILE_NAMES.length).fill(null));
  const [dragging, setDragging] = useState(null);

  // Progression
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('idle');          // 'upload' | 'scripts' | 'done'
  const [uploadQueue, setUploadQueue] = useState([]);  // index des fichiers à envoyer
  const [uploadIndex, setUploadIndex] = useState(0);   // position dans uploadQueue
  const [scriptsDone, setScriptsDone] = useState(0);
  const [currentScript, setCurrentScript] = useState('');
  const [scriptError, setScriptError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    document.title = 'MYSSA – Drag & Drop';
  }, []);

  const esRef = useRef(null);
  useEffect(() => () => esRef.current?.close(), []);

  /* ---------- Dépôt / sélection ---------- */
  const setFileAt = (i, file) => {
    setFiles(prev => {
      const n = [...prev];
      n[i] = file;
      return n;
    });
  };

  const handleDrop = (i, e) => {
    e.preventDefault();
    setDragging(null);
    if (isRunning) return;
    const file = e.dataTransfer.files[0];
    if (file) setFileAt(i, file);
  };

  const handleDragOver = (i, e) => { e.preventDefault(); if (!isRunning) setDragging(i); };
  const handleDragLeave = () => setDragging(null);

  const handleClick = (i) => {
    if (isRunning) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) setFileAt(i, file);
    };
    input.click();
  };

  const handleKey = (i, e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(i); }
  };

  const handleRemove = (i, e) => {
    e.stopPropagation();
    if (isRunning) return;
    setFileAt(i, null);
  };

  const fermerModal = () => {
    setIsRunning(false); setProgress(0); setPhase('idle');
    setUploadQueue([]); setUploadIndex(0);
    setScriptsDone(0); setCurrentScript('');
    setScriptError(false); setErrorMsg('');
  };

  /* ---------- Envoi + traitement ---------- */
  const handleUploadAll = async () => {
    const filled = files.map((f, i) => (f ? i : null)).filter(i => i !== null);
    if (filled.length === 0) return;

    // Ouvre la modale immédiatement
    setIsRunning(true);
    setProgress(0);
    setPhase('upload');
    setUploadQueue(filled);
    setUploadIndex(0);
    setScriptsDone(0);
    setCurrentScript('');
    setScriptError(false);
    setErrorMsg('');

    // Phase 1 : uploads (0 → PART_UPLOAD %)
    try {
      for (let n = 0; n < filled.length; n++) {
        const i = filled[n];
        setUploadIndex(n);
        const formData = new FormData();
        formData.append('file', files[i]);
        formData.append('newName', SAVE_NAMES[i]);
        await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setProgress(Math.round(((n + 1) / filled.length) * PART_UPLOAD));
      }
      setUploadIndex(filled.length);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Erreur inconnue';
      setErrorMsg(`Erreur upload : ${msg}`);
      setScriptError(true);
      return; // la modale reste ouverte pour afficher l'erreur
    }

    // Phase 2 : SSE run-all (PART_UPLOAD → 100 %)
    setPhase('scripts');
    esRef.current?.close();
    const es = new EventSource(`${API}/api/run-all`);
    esRef.current = es;

    let done = 0;

    es.onmessage = (event) => {
      const raw = event.data;

      if (raw === '__DONE__') {
        es.close();
        setProgress(100);
        setScriptsDone(TOTAL_SCRIPTS);
        setPhase('done');
        return;
      }

      if (raw.startsWith('ERREUR:')) {
        setErrorMsg(raw.replace('ERREUR:', '').trim());
        setScriptError(true);
        es.close();
        return;
      }

      if (raw.startsWith('▶ Démarrage de')) {
        const name = raw.replace('▶ Démarrage de ', '').replace('...', '').trim();
        setCurrentScript(name);
      }

      if (raw.includes('terminé avec succès')) {
        done++;
        setScriptsDone(done);
        setProgress(PART_UPLOAD + Math.round((Math.min(done, TOTAL_SCRIPTS) / TOTAL_SCRIPTS) * (100 - PART_UPLOAD)));
      }
    };

    es.onerror = () => {
      es.close();
      setErrorMsg('Connexion interrompue');
      setScriptError(true);
    };
  };

  /* ---------- Étapes affichées dans la modale ---------- */
  const isDone = phase === 'done';

  const etapes = [
    ...uploadQueue.map((fi, n) => {
      const done = phase !== 'upload' || n < uploadIndex;
      const current = phase === 'upload' && n === uploadIndex;
      return {
        key: `up-${fi}`,
        label: `Envoi · ${FILE_NAMES[fi]}`,
        sub: files[fi]?.name || '',
        done,
        current: current && !scriptError,
        failed: current && scriptError,
      };
    }),
    {
      key: 'scripts',
      label: 'Traitement des scripts',
      sub: phase === 'scripts'
        ? `${scriptsDone}/${TOTAL_SCRIPTS}${currentScript ? ` · ${currentScript}` : ''}`
        : isDone ? `${TOTAL_SCRIPTS}/${TOTAL_SCRIPTS} scripts` : `${TOTAL_SCRIPTS} scripts`,
      done: isDone,
      current: phase === 'scripts' && !scriptError,
      failed: phase === 'scripts' && scriptError,
      scripts: phase === 'scripts' || isDone,
    },
    {
      key: 'final',
      label: 'Finalisation',
      sub: '',
      done: isDone,
      current: false,
      failed: false,
    },
  ];

  let titre = 'Initialisation…';
  if (scriptError) titre = errorMsg || 'Erreur détectée';
  else if (isDone) titre = 'Traitement terminé avec succès';
  else if (phase === 'upload' && uploadQueue.length) titre = `Envoi de ${FILE_NAMES[uploadQueue[Math.min(uploadIndex, uploadQueue.length - 1)]]}…`;
  else if (phase === 'scripts') titre = currentScript ? `Exécution : ${currentScript}` : 'Lancement des scripts…';

  const nbFilled = files.filter(f => f !== null).length;

  /* ===================================================
     RENDU
     =================================================== */
  return (
    <div className="dd-page">

      {/* ── Modale de progression ── */}
      {isRunning && (
        <div className="dd-overlay">
          <div className="dd-modal" role="dialog" aria-modal="true" aria-label="Mise à jour des données">
            <div className={`dd-modal-head ${scriptError ? 'error' : ''}`}>
              <div className="dd-modal-kicker">Mise à jour des données</div>
              <div className="dd-modal-title-row">
                <span className="dd-modal-title">{titre}</span>
                <span className="dd-modal-pct">{progress}%</span>
              </div>
              <div className="dd-modal-track"><div className="dd-modal-fill" style={{ width: `${progress}%` }} /></div>
            </div>

            <div className="dd-steps">
              {etapes.map(st => (
                <div key={st.key} className={`dd-step ${st.done ? 'done' : ''} ${st.current ? 'current' : ''} ${st.failed ? 'failed' : ''}`}>
                  <span className="dd-step-icon">
                    {st.done && <IconCheck />}
                    {st.failed && <IconCross />}
                  </span>
                  <div className="dd-step-body">
                    <span className="dd-step-label">{st.label}</span>
                    {st.sub && <span className="dd-step-sub">{st.sub}</span>}
                    {st.scripts && (
                      <div className="dd-step-track">
                        <div className="dd-step-fill" style={{ width: `${(Math.min(scriptsDone, TOTAL_SCRIPTS) / TOTAL_SCRIPTS) * 100}%` }} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="dd-modal-foot">
              {(isDone || scriptError) ? (
                <button type="button" className={`dd-btn ${scriptError ? 'dd-btn-danger' : 'dd-btn-dark'}`} onClick={fermerModal}>
                  {scriptError ? 'Fermer (erreur)' : 'Fermer'}
                </button>
              ) : (
                <span className="dd-muted">Traitement en cours, ne ferme pas la page…</span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="dd-container">

        {/* ── Header ── */}
        <div className="dd-header">
          <div className="dd-header-left">
            <div className="dd-title-row">
              <div className="dd-accent" />
              <h1 className="dd-title">Import des fichiers clients</h1>
            </div>
            <div className="dd-subtitle">
              {nbFilled === 0
                ? ''
                : `${nbFilled} fichier${nbFilled > 1 ? 's' : ''} prêt${nbFilled > 1 ? 's' : ''} sur ${FILE_NAMES.length} · les scripts de calcul seront relancés après l'envoi.`}
            </div>
          </div>

          <button
            type="button"
            className="dd-btn dd-btn-primary"
            onClick={handleUploadAll}
            disabled={isRunning || nbFilled === 0}
          >
            <IconCheck size={14} width={2} />
            {isRunning ? 'En cours…' : `Enregistrer et lancer (${nbFilled})`}
          </button>
        </div>

        {/* ── Progression du remplissage ── */}
        <div className="dd-fill">
          <div className="dd-fill-track">
            <div className="dd-fill-bar" style={{ width: `${(nbFilled / FILE_NAMES.length) * 100}%` }} />
          </div>
          <span className="dd-fill-label">{nbFilled}/{FILE_NAMES.length}</span>
        </div>

        {/* ── Grille des zones de dépôt ── */}
        <div className="dd-grid">
          {FILE_NAMES.map((name, i) => {
            const file = files[i];
            const isFilled = !!file;
            const isOver = dragging === i;
            return (
              <div
                key={name}
                role="button"
                tabIndex={isRunning ? -1 : 0}
                aria-label={isFilled ? `${name} : ${file.name}. Cliquer pour remplacer` : `${name} : déposer ou sélectionner un fichier`}
                className={`dd-box ${isFilled ? 'filled' : ''} ${isOver ? 'over' : ''} ${isRunning ? 'locked' : ''}`}
                onClick={() => handleClick(i)}
                onKeyDown={(e) => handleKey(i, e)}
                onDrop={(e) => handleDrop(i, e)}
                onDragOver={(e) => handleDragOver(i, e)}
                onDragLeave={handleDragLeave}
              >
                {isFilled && !isRunning && (
                  <button
                    type="button"
                    className="dd-remove"
                    aria-label={`Retirer le fichier ${name}`}
                    onClick={(e) => handleRemove(i, e)}
                  >
                    <IconCross size={11} />
                  </button>
                )}

                <span className="dd-box-icon">
                  {isFilled ? <IconCheck size={20} width={2.4} /> : <Icon path={ICON_PATHS[name]} />}
                </span>
                <span className="dd-box-name">{name}</span>
                {isFilled ? (
                  <>
                    <span className="dd-box-file" title={file.name}>{file.name}</span>
                    <span className="dd-box-size">{(file.size / 1024).toFixed(0)} Ko</span>
                  </>
                ) : (
                  <span className="dd-box-hint">{isOver ? 'Relâcher pour déposer' : 'Glisser ou cliquer'}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Légende ── */}
        <div className="dd-legend">
          <span><i className="dd-legend-box dd-legend-filled" />Fichier chargé</span>
          <span><i className="dd-legend-box dd-legend-empty" />En attente</span>
        </div>
      </div>
    </div>
  );
};

export default Dragdrop;